import { IDocumentData } from "../../../../core/_models";
import type { StepTab } from "../../ProjectInitiation-steps/core/_models";

// Economic & Commercial Viability tabs, one per sub-section.
export const ECONOMIC_VIABILITY_TABS = {
    value_for_money: 1,
    benefit_analysis: 2,
    risk_evaluation: 3,
    economic_multipliers: 4,
    economies_of_scale: 5,
    cost_effectiveness: 6,
    social_economic: 7,
} as const;

// Tab-wise steps, keyed by step, with every tab in display order. Each tab saves its own
// fields to its project-fields sub-section.
export const STEP_TABS: Partial<Record<string, ReadonlyArray<StepTab>>> = {
    economic_and_commercial_viability: [
        { tabId: ECONOMIC_VIABILITY_TABS.value_for_money, title: 'Value-for-Money Assessment', section: 'ecv_value_for_money_assessment', fields: ['value_for_money_assessment', 'summary_vfm_findings'] },
        { tabId: ECONOMIC_VIABILITY_TABS.benefit_analysis, title: 'Benefit Analysis', section: 'ecv_benefit_analysis', fields: ['benefit_analysis', 'summary_benefit_findings'] },
        { tabId: ECONOMIC_VIABILITY_TABS.risk_evaluation, title: 'Risk Evaluation & Contingent Liability', section: 'ecv_risk_evaluation_contingent_liability', fields: ['risk_evaluation_and_contingent_liability', 'summary_risk_findings'] },
        { tabId: ECONOMIC_VIABILITY_TABS.economic_multipliers, title: 'Economic Multipliers & Macroeconomic Benefits', section: 'ecv_economic_multipliers', fields: ['economic_multipliers_and_macroeconomic_benefits', 'summary_economic_multiplier_findings'] },
        { tabId: ECONOMIC_VIABILITY_TABS.economies_of_scale, title: 'Economies of Scale', section: 'ecv_economies_of_scale', fields: ['economies_of_scale', 'summary_economies_of_scale_findings'] },
        { tabId: ECONOMIC_VIABILITY_TABS.cost_effectiveness, title: 'Cost-Effectiveness Analysis (CEA)', section: 'ecv_cost_effectiveness_analysis', fields: ['cost_effectiveness_assessment', 'summary_cea_findings'] },
        { tabId: ECONOMIC_VIABILITY_TABS.social_economic, title: 'Social & Economic Analysis', section: 'ecv_social_economic_analysis', fields: ['social_economic_assessment', 'summary_social_economic_findings'] },
    ],
};


export interface IProjectStatusAndReadiness {
    current_development_phase: string;
    readiness_for_procurement: string;
    regulatory_approvals_status: string[];
    other_regulatory_approval: string;
    stakeholder_engagement_status: string;
    procurement_readiness: string;
    regulatory_readiness: string;
    stakeholder_readiness: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IImplementationReadiness {
    imple_timeline_planning_start: null | Date;
    imple_timeline_planning_end: null | Date;

    imple_timeline_procurement_start: null | Date;
    imple_timeline_procurement_end: null | Date;

    imple_timeline_delivery_start: null | Date;
    imple_timeline_delivery_end: null | Date;

    imple_timeline_operational_start: null | Date;
    imple_timeline_operational_end: null | Date;

    key_constraints: string[];

    delivery_model_confidence_score: number;


    formAction?: string;
    iscompleted?: boolean;

}

export interface IStakeholderCommunicationReadiness {
    investor_engagement_status: string;
    community_participation_status: string;
    government_coordination_level: string;
    formAction?: string;
    iscompleted?: boolean;
}

// Each sub-section (tab) has an analysis write-up and a summary of SG1 & SG2 findings.
export interface IEconomicAndCommercialViability {
    value_for_money_assessment: string;
    summary_vfm_findings: string;
    benefit_analysis: string;
    summary_benefit_findings: string;
    risk_evaluation_and_contingent_liability: string;
    summary_risk_findings: string;
    economic_multipliers_and_macroeconomic_benefits: string;
    summary_economic_multiplier_findings: string;
    economies_of_scale: string;
    summary_economies_of_scale_findings: string;
    cost_effectiveness_assessment: string;
    summary_cea_findings: string;
    social_economic_assessment: string;
    summary_social_economic_findings: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IFeasibilityOption {
    row_id: number | null;
    option_name: string;
    description: string;
    capital_cost: number;
    operating_cost: number;
    delivery_time: number;
    expected_benefits: string;
    strategic_alignment: number;
    complexity: number;
    risk_rating: number;
    score: number;
}

// Row returned by the stage-neutral /project/options/* API.
export interface IProjectOptionRow extends IFeasibilityOption {
    project_id: number;
    stage_id: string;
    option_analysis_description: string | null;
}

export interface IProjectFeasibilityAndOptionsAnalysis {
    justification_of_option: Record<string, string>;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IRiskAndQualityGate {
    // Entered from the offline panoptic evaluation (0 - 100); shown only, not part of any calculation.
    panoptic_evaluation_score: number | '';
    formAction?: string;
    iscompleted?: boolean;
}

// Stage 3 labels for the Stage 2 Demand and Capacity Validation values, shown read-only.
export interface IDemandAndNeedsValidationEngine {
    current_demand_units: number | '';
    future_demand_horizon_year: number | '';
    population_served: number | '';
    growth_rate: number | '';
    peak_capacity_required: number | '';
    demand_confidence_level: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface ICbaCeaIntelligenceEngine {
    capital_cost: number | '';
    annual_operating_cost: number | '';
    economic_benefits: number | '';
    social_benefits: number | '';
    residual_value: number | '';
    discount_rate: number | '';
    analysis_period_years: number | '';
    cost_per_beneficiary: number;
    cost_per_household: number;
    cost_per_service_unit: number;
    cost_per_job_created: number;
    cost_per_kilometre: number;
    cost_per_outcome_achieved: number;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IRiskAndSensitivityAnalysisEngine {
    capex_scenario: number | '';
    demand_scenario: number | '';
    inflation_scenario: number | '';
    revenue_scenario: number | '';
    schedule_delay_months: number | '';
    formAction?: string;
    iscompleted?: boolean;
}

export interface ISummaryMatrix {
    key: string;
    label: string;
    subtitle: string;
    npv: number;
    bcr: number;
}

export type IPrefeasibilityTypes =
    IProjectStatusAndReadiness
    | IImplementationReadiness
    | IStakeholderCommunicationReadiness
    | IRiskAndQualityGate
    | IDocumentData
    | IEconomicAndCommercialViability
    | IProjectFeasibilityAndOptionsAnalysis
    | IDemandAndNeedsValidationEngine
    | ICbaCeaIntelligenceEngine
    | IRiskAndSensitivityAnalysisEngine
